// ZATCA Setup — one row per branch (API ZatcaController, DB script Section AQ).
// Covers the web Sales Invoices and Sales Returns only; POS bills stay with the
// Windows POS. The branch's certificate, private key and CSIDs come from its
// CertificateInfo.json: the user browses for the file, the browser reads it and
// the API stores it. Seller name, VAT / CR numbers and the national address are
// typed here; switching ZATCA on starts signing and sending the branch's sales
// invoices and returns from the start date.
// Logic ported from the Inventory product's ZATCA Setup; the layout follows this app.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { OverlayTrigger } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import LoadingUtils from "../../core/utils/LoadingUtils";
import { setheadertoggle } from "../../redux/toggle";
import { renderTooltip } from "../../redux/helper";
import {
  getZatcaSetups,
  importZatcaCertificate,
  saveZatcaSetup,
  zatcaError,
} from "../../services/ZatcaApiServices";

// as the ZATCA SDK / CertificateInfo.json number them
const ENVIRONMENTS = [
  { value: 2, label: "Developer portal (testing)" },
  { value: 1, label: "Simulation" },
  { value: 0, label: "Production" },
];
const envLabel = (v) => ENVIRONMENTS.find((e) => e.value === Number(v))?.label || "—";

const FIELDS = [
  "isEnabled",
  "environmentType",
  "startDate",
  "sellerName",
  "vatNumber",
  "crNumber",
  "streetName",
  "buildingNumber",
  "citySubdivision",
  "cityName",
  "postalZone",
  "countryCode",
];

const toForm = (s) => ({
  isEnabled: !!s?.isEnabled,
  environmentType: s?.environmentType ?? 2,
  startDate: s?.startDate ? dayjs(s.startDate).format("YYYY-MM-DD") : "",
  sellerName: s?.sellerName || "",
  vatNumber: s?.vatNumber || "",
  crNumber: s?.crNumber || "",
  streetName: s?.streetName || "",
  buildingNumber: s?.buildingNumber || "",
  citySubdivision: s?.citySubdivision || "",
  cityName: s?.cityName || "",
  postalZone: s?.postalZone || "",
  countryCode: s?.countryCode || "SA",
});

const Field = ({ label, required, hint, col = "col-md-6", children }) => (
  <div className={col}>
    <div className="input-blocks">
      <label>
        {label}
        {required ? <span className="text-danger"> *</span> : null}
      </label>
      {children}
      {hint ? <small className="text-muted d-block mt-1">{hint}</small> : null}
    </div>
  </div>
);

const Fact = ({ k, v, s }) => (
  <div className="col-md-3 col-6 mb-2">
    <div className="text-muted small">{k}</div>
    <div className="fw-semibold">{v}</div>
    {s ? <div className="small text-warning">{s}</div> : null}
  </div>
);

const ZatcaSetup = () => {
  const dispatch = useDispatch();
  const { togglehead } = useSelector((state) => state.headerToggle);
  const { userId } = useSelector((state) => state.userauth);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [branchId, setBranchId] = useState();
  const [form, setForm] = useState(toForm(null));
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSetups(await getZatcaSetups());
    } catch (error) {
      toast.error(zatcaError(error, "Could not load the ZATCA setup"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return setups
      .filter(
        (s) =>
          !needle ||
          `${s.branchName}`.toLowerCase().includes(needle) ||
          `${s.vatNumber ?? ""}`.toLowerCase().includes(needle) ||
          `${s.sellerName ?? ""}`.toLowerCase().includes(needle)
      )
      .slice()
      .sort((a, b) =>
        String(a.branchName ?? "").localeCompare(String(b.branchName ?? ""), undefined, { sensitivity: "base" })
      );
  }, [setups, searchText]);

  const selected = setups.find((s) => s.branchID === branchId);

  // a fresh copy of the picked branch whenever it (or its saved data) changes
  useEffect(() => {
    setForm(toForm(selected));
  }, [selected]);

  const saved = toForm(selected);
  const dirty = !!selected && FIELDS.some((f) => `${form[f]}` !== `${saved[f]}`);
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await saveZatcaSetup({
        branchID: selected.branchID,
        ...form,
        environmentType: Number(form.environmentType),
        startDate: form.startDate || null,
        updatedUser: userId?.id ? `${userId.id}` : null,
      });
      toast.success(form.isEnabled ? "Saved — ZATCA is on for this branch" : "Saved");
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not save the ZATCA setup"));
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selected) return;
    if (file.size > 500000) {
      toast.error("That file is too large to be a CertificateInfo.json");
      return;
    }
    setSaving(true);
    try {
      const content = await file.text();
      const result = await importZatcaCertificate({
        branchID: selected.branchID,
        fileName: file.name,
        content,
        updatedUser: userId?.id ? `${userId.id}` : null,
      });
      toast.success(
        `Certificate imported (${envLabel(result.environmentType)}). ` +
          (result.chainFromFile
            ? `The invoice counter continues from ${result.icv}.`
            : `The branch keeps its own invoice counter (${result.icv}).`),
        { duration: 6000 }
      );
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not import the certificate"), { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const validTo = selected?.certificateValidTo ? dayjs(selected.certificateValidTo) : null;
  const expiresSoon = validTo && validTo.diff(dayjs(), "day") < 30;

  return (
    <div>
      <LoadingUtils loading={loading || saving} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>ZATCA Setup</h4>
                <h6>E-invoicing (Phase 2) for web Sales Invoices and Returns, per branch</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                  <Link onClick={load}>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                  <Link
                    id="collapse-header"
                    className={togglehead ? "active" : ""}
                    onClick={() => dispatch(setheadertoggle(!togglehead))}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="row">
            {/* branches */}
            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <input
                    type="search"
                    className="form-control mb-3"
                    placeholder="Branch, seller, VAT number…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <div className="list-group">
                    {list.map((s) => (
                      <button
                        type="button"
                        key={s.branchID}
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                          s.branchID === branchId ? "active" : ""
                        }`}
                        onClick={() => setBranchId(s.branchID === branchId ? undefined : s.branchID)}
                      >
                        <span>
                          <span className="d-block fw-semibold">{s.branchName}</span>
                          <small className={s.branchID === branchId ? "" : "text-muted"}>
                            {s.vatNumber || "No VAT number"} ·{" "}
                            {s.hasCertificate ? envLabel(s.environmentType) : "No certificate"}
                          </small>
                        </span>
                        {s.isEnabled ? (
                          <span className="badge bg-success">ZATCA on</span>
                        ) : (
                          <span className="badge bg-secondary">{s.hasCertificate ? "Off" : "Not set up"}</span>
                        )}
                      </button>
                    ))}
                    {list.length === 0 && !loading ? (
                      <div className="text-muted p-2">No branch matches.</div>
                    ) : null}
                  </div>
                  <div className="text-muted small mt-3">
                    {setups.filter((s) => s.isEnabled).length} of {setups.length} branches on ZATCA
                  </div>
                </div>
              </div>
            </div>

            {/* the picked branch */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  {!selected ? (
                    <div className="text-center text-muted py-5">
                      <h5>Pick a branch</h5>
                      Choose a branch on the left to import its ZATCA certificate and fill in its seller details.
                    </div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="mb-1">{selected.branchName}</h5>
                          <div className="text-muted">
                            {selected.isEnabled
                              ? `ZATCA on since ${
                                  selected.startDate ? dayjs(selected.startDate).format("DD MMM YYYY") : "—"
                                } · ${envLabel(selected.environmentType)}`
                              : "ZATCA off"}
                          </div>
                        </div>
                        {dirty ? <span className="badge bg-warning text-dark">Unsaved</span> : null}
                      </div>

                      <h6 className="border-bottom pb-2 mb-3">Certificate (CertificateInfo.json)</h6>
                      <div className="row">
                        <Fact
                          k="File"
                          v={
                            selected.hasCertificate
                              ? selected.certificateFileName || "CertificateInfo.json"
                              : "Not imported"
                          }
                          s={
                            selected.certificateImportedDate
                              ? `imported ${dayjs(selected.certificateImportedDate).format("DD MMM YYYY, hh:mm A")}`
                              : null
                          }
                        />
                        <Fact k="Environment" v={selected.hasCertificate ? envLabel(selected.environmentType) : "—"} />
                        <Fact
                          k="Valid to"
                          v={validTo ? validTo.format("DD MMM YYYY") : "—"}
                          s={expiresSoon ? "renew the certificate soon" : null}
                        />
                        <Fact k="Invoice counter (ICV)" v={selected.hasCertificate ? selected.icv : "—"} />
                      </div>
                      {selected.certificateSubject ? (
                        <div className="small text-muted mb-2">{selected.certificateSubject}</div>
                      ) : null}
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".json,application/json"
                        style={{ display: "none" }}
                        onChange={handleFile}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm mb-2"
                        disabled={saving}
                        onClick={() => fileRef.current?.click()}
                      >
                        {selected.hasCertificate ? "Replace CertificateInfo.json" : "Import CertificateInfo.json"}
                      </button>
                      <div className="small text-muted mb-4">
                        Pick this branch&apos;s CertificateInfo.json (written when the branch was onboarded with
                        ZATCA). The certificate, private key and CSIDs are stored for this branch only; the invoice
                        counter is taken from the file until the branch has signed its first invoice here. Use a
                        certificate of its own for the web: never the one the branch&apos;s Windows POS uses, or the
                        two invoice chains clash.
                      </div>

                      <h6 className="border-bottom pb-2 mb-3">E-invoicing</h6>
                      <div className="row">
                        <div className="col-12 mb-3">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="zatca-enabled"
                              checked={form.isEnabled}
                              onChange={set("isEnabled")}
                              disabled={!selected.hasCertificate}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="zatca-enabled">
                              ZATCA on for this branch
                            </label>
                          </div>
                          <small className="text-muted">
                            Sales invoices and returns dated from the start date are signed and sent to ZATCA. A
                            signed invoice can no longer be edited or deleted.
                          </small>
                        </div>
                        <Field label="Environment" required>
                          <select
                            className="form-select"
                            value={form.environmentType}
                            onChange={set("environmentType")}
                          >
                            {ENVIRONMENTS.map((e) => (
                              <option key={e.value} value={e.value}>
                                {e.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Start date" required hint="Invoices dated on or after this go to ZATCA">
                          <input
                            type="date"
                            className="form-control"
                            value={form.startDate}
                            onChange={set("startDate")}
                          />
                        </Field>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 mt-2">Seller</h6>
                      <div className="row">
                        <Field label="Seller name (as registered)" required col="col-md-12">
                          <input
                            type="text"
                            className="form-control"
                            value={form.sellerName}
                            onChange={set("sellerName")}
                            maxLength={200}
                          />
                        </Field>
                        <Field label="VAT number" required hint="15 digits, starts and ends with 3">
                          <input
                            type="text"
                            className="form-control"
                            value={form.vatNumber}
                            onChange={set("vatNumber")}
                            maxLength={15}
                            inputMode="numeric"
                          />
                        </Field>
                        <Field label="CR number" required>
                          <input
                            type="text"
                            className="form-control"
                            value={form.crNumber}
                            onChange={set("crNumber")}
                            maxLength={20}
                          />
                        </Field>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 mt-2">National address</h6>
                      <div className="row">
                        <Field label="Street" required col="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            value={form.streetName}
                            onChange={set("streetName")}
                            maxLength={150}
                          />
                        </Field>
                        <Field label="Building number" required hint="4 digits" col="col-md-4">
                          <input
                            type="text"
                            className="form-control"
                            value={form.buildingNumber}
                            onChange={set("buildingNumber")}
                            maxLength={4}
                            inputMode="numeric"
                          />
                        </Field>
                        <Field label="District" required col="col-md-4">
                          <input
                            type="text"
                            className="form-control"
                            value={form.citySubdivision}
                            onChange={set("citySubdivision")}
                            maxLength={150}
                          />
                        </Field>
                        <Field label="City" required col="col-md-4">
                          <input
                            type="text"
                            className="form-control"
                            value={form.cityName}
                            onChange={set("cityName")}
                            maxLength={100}
                          />
                        </Field>
                        <Field label="Postal code" required hint="5 digits" col="col-md-2">
                          <input
                            type="text"
                            className="form-control"
                            value={form.postalZone}
                            onChange={set("postalZone")}
                            maxLength={5}
                            inputMode="numeric"
                          />
                        </Field>
                        <Field label="Country" col="col-md-2">
                          <input
                            type="text"
                            className="form-control"
                            value={form.countryCode}
                            onChange={set("countryCode")}
                            maxLength={2}
                          />
                        </Field>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 mt-2">Invoices</h6>
                      <div className="row">
                        <Fact k="Signed" v={selected.signedCount} />
                        <Fact k="Accepted by ZATCA" v={selected.doneCount} />
                        <Fact k="Waiting to send" v={selected.pendingCount} />
                        <Fact k="Rejected" v={selected.rejectedCount} />
                      </div>
                      <div className="small text-muted mb-3">
                        B2B buyers: a customer with a Saudi VAT number gets a standard (cleared) invoice. Fill in the
                        customer&apos;s Address 1 = street, Address 2 = district, Address 3 = building number, City and
                        PIN = postal code.
                      </div>

                      <div className="d-flex justify-content-end align-items-center gap-2 border-top pt-3">
                        <span className="me-auto small text-muted">
                          {!selected.hasCertificate
                            ? "Import the branch's CertificateInfo.json before switching ZATCA on."
                            : dirty
                            ? "You have unsaved changes."
                            : ""}
                        </span>
                        <button
                          type="button"
                          className="btn btn-cancel"
                          disabled={!dirty || saving}
                          onClick={() => setForm(toForm(selected))}
                        >
                          Undo
                        </button>
                        <button
                          type="button"
                          className="btn btn-submit"
                          disabled={!dirty || saving}
                          onClick={handleSave}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZatcaSetup;
